/**
 * Optimization Job Adapter
 * 
 * Maps between the existing OptimizationJob type used by OptimizationEventService
 * and the new unified Job type used by the Jobs system.
 */

import type { Job, JobType, JobStatus, JobProgress, JobTiming } from '../../types/jobs';
import type { OptimizationJob, OptimizationEvent, OptimizationProgress } from '../optimization/OptimizationEventService';

/**
 * Maps optimization status string to JobStatus enum
 */
export function mapOptimizationStatusToJobStatus(
    status: OptimizationJob['status'] | string
): JobStatus {
    const statusMap: Record<string, JobStatus> = {
        'queued': 'QUEUED',
        'running': 'RUNNING',
        'completed': 'COMPLETED',
        'failed': 'FAILED',
        'cancelled': 'CANCELLED',
    };
    return statusMap[status] ?? 'QUEUED';
}

/**
 * Determines the phase string based on job status
 */
function getPhaseForStatus(status: JobStatus): string {
    switch (status) {
        case 'QUEUED':
            return 'Queued';
        case 'INITIALIZING':
            return 'Initializing';
        case 'RUNNING':
        case 'CANCEL_REQUESTED':
            return 'Optimizing';
        case 'COMPLETED':
            return 'Complete';
        case 'CANCELLED':
            return 'Cancelled';
        case 'FAILED':
            return 'Failed';
        case 'ABORTED':
            return 'Aborted';
        default:
            return 'Unknown';
    }
}

/**
 * Maps optimization job type to our JobType
 */
function mapJobType(jobType: OptimizationJob['jobType']): JobType {
    if (jobType === 'walk-forward') {
        return 'WFA_RUN';
    }
    return 'OPTIMIZATION_RUN';
}

/**
 * Maps a full OptimizationJob to our unified Job type
 */
export function mapOptimizationJobToJob(optJob: OptimizationJob): Job {
    const status = mapOptimizationStatusToJobStatus(optJob.status);
    const phase = getPhaseForStatus(status);
    
    const progress: JobProgress = {
        current: optJob.currentCombination ?? 0,
        total: optJob.totalCombinations ?? 0,
        percent: optJob.progress ?? 0,
        phase,
    };
    
    const timing: JobTiming = {
        createdAt: optJob.createdAt,
        startedAt: optJob.startedAt,
        endedAt: optJob.completedAt,
    };
    
    return {
        id: optJob.id,
        type: mapJobType(optJob.jobType),
        status,
        priority: optJob.priority,
        userId: optJob.userId,
        context: {
            symbol: optJob.parameters.symbol,
            timeframe: optJob.parameters.interval,
            strategyName: optJob.parameters.strategy,
        },
        progress,
        timing,
    };
}

/**
 * Partial job update from an OptimizationEvent
 */
export interface JobUpdate {
    id: string;
    status?: JobStatus;
    progress?: Partial<JobProgress>;
    timing?: Partial<JobTiming>;
}

/**
 * Maps an OptimizationEvent to a JobUpdate
 */
export function mapOptimizationEventToJobUpdate(event: OptimizationEvent): JobUpdate {
    const update: JobUpdate = { id: event.jobId };
    
    switch (event.type) {
        case 'progress': {
            const progressData = event.data as OptimizationProgress;
            // Progress events mean the job is running
            update.status = 'RUNNING';
            update.progress = {
                current: progressData.current,
                total: progressData.total,
                percent: progressData.progress,
                phase: 'Optimizing',
            };
            break;
        }
        case 'completed': {
            update.status = 'COMPLETED';
            update.progress = {
                phase: 'Complete',
                percent: 100,
            };
            update.timing = {
                endedAt: new Date().toISOString(),
            };
            break;
        }
        case 'failed': {
            update.status = 'FAILED';
            update.progress = {
                phase: 'Failed',
            };
            update.timing = {
                endedAt: new Date().toISOString(),
            };
            break;
        }
        case 'cancelled': {
            update.status = 'CANCELLED';
            update.progress = {
                phase: 'Cancelled',
            };
            update.timing = {
                endedAt: new Date().toISOString(),
            };
            break;
        }
        case 'queued': {
            update.status = 'QUEUED';
            update.progress = {
                phase: 'Queued',
            };
            break;
        }
    }
    
    return update;
}

/**
 * Parameters to create a new Job from optimization start
 */
export interface CreateJobFromOptimizationParams {
    jobId: string;
    symbol: string;
    interval: string;
    strategy: string;
    totalCombinations: number;
    jobType?: JobType;
}

/**
 * Creates a new Job from optimization start request parameters
 */
export function createJobFromOptimization(params: CreateJobFromOptimizationParams): Job {
    return {
        id: params.jobId,
        type: params.jobType ?? 'OPTIMIZATION_RUN',
        status: 'QUEUED',
        context: {
            symbol: params.symbol,
            timeframe: params.interval,
            strategyName: params.strategy,
        },
        progress: {
            current: 0,
            total: params.totalCombinations,
            percent: 0,
            phase: 'Queued',
        },
        timing: {
            createdAt: new Date().toISOString(),
        },
    };
}
